import { ERC20__factory, getProvider, vaultService } from '@optimex-xyz/trade-wallet-sdk'

import { IToken } from '@/types'
import { isEvmChain } from '@/utils/chain'
import { delay } from '@/utils/common'

export interface BaseTransactionOptions {
  networkId: string
  toAddress: string
  amount: number | bigint | string
  options?: any
}

export interface TransactionTracker {
  trackTransaction(controller: AbortController): Promise<string | null>
}

export abstract class TransactionHelper<TOptions extends BaseTransactionOptions> {
  protected readonly controller = new AbortController()
  protected readonly timeoutMs = 310000 // 5mins10s

  async handleTransaction(options: TOptions): Promise<string> {
    const timeoutPromise = this.createTimeoutPromise()
    const sendPromise = this.sendTransaction(options)
    const trackPromise = this.createTracker(options)
      .trackTransaction(this.controller)
      .then((txId) => txId || null)

    try {
      const winningTx = await Promise.race([sendPromise, trackPromise, timeoutPromise])

      this.controller.abort()

      if (winningTx === null) {
        return await Promise.race([sendPromise, timeoutPromise])
      }

      return winningTx
    } catch (error) {
      this.controller.abort()
      throw error
    } finally {
      if (!this.controller.signal.aborted) {
        this.controller.abort()
      }
    }
  }

  protected createTimeoutPromise(): Promise<never> {
    return new Promise<never>((_, reject) => {
      setTimeout(() => {
        this.controller.abort()
        reject(new Error(`${this.getNetworkId()} transaction timeout.`))
      }, this.timeoutMs)
    })
  }

  protected getNetworkId(): string {
    return this.options?.networkId || ''
  }

  protected abstract createTracker(options: TOptions): TransactionTracker

  protected abstract sendTransaction(options: TOptions): Promise<string>

  constructor(protected options?: TOptions) {}
}

export async function withRetry<T>(
  action: () => Promise<T | null>,
  options: {
    maxRetries?: number
    retryDelay?: number
    initialDelay?: number
    controller?: AbortController
  } = {}
): Promise<T | null> {
  const { maxRetries = 120, retryDelay = 5000, initialDelay = 5000, controller } = options

  await delay(initialDelay)

  let attempts = 0
  const lastResult: T | null = null

  while (attempts < maxRetries) {
    try {
      if (controller?.signal.aborted) {
        return lastResult
      }

      const result = await action()

      if (result) {
        return result
      }

      attempts++
      if (attempts < maxRetries) {
        await delay(retryDelay)
      }
    } catch {
      attempts++
      if (attempts < maxRetries) {
        await delay(retryDelay)
      }
    }
  }

  return lastResult
}

export const getAllowance = async ({
  walletAddress,
  fromToken,
  spenderAddress,
}: {
  walletAddress: string
  fromToken: { tokenAddress: string; networkId: string }
  spenderAddress: string
}) => {
  if (isEvmChain(fromToken.networkId) && fromToken.tokenAddress !== 'native') {
    const assetProvider = getProvider(fromToken.networkId)

    const erc20Contract = ERC20__factory.connect(fromToken.tokenAddress, assetProvider)

    const currentAllowance = await erc20Contract.allowance(walletAddress, spenderAddress)
    return currentAllowance || 0n
  }
  return null
}

export const getAllowanceForVault = async (walletAddress: string, fromToken: IToken) => {
  if (isEvmChain(fromToken.networkId) && fromToken.tokenAddress !== 'native') {
    const vaultContractAddress = await vaultService.getVaultContractAddress(fromToken)
    return getAllowance({ walletAddress, fromToken, spenderAddress: vaultContractAddress })
  }
  return null
}
